import * as Sentry from "@sentry/react";
import type { ErrorEvent } from "@sentry/react";
import type { Breadcrumb } from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import {
  currentBrowserLocation,
  hasSensitiveLocation,
  isSensitiveLocation,
} from "./sensitive-location";

type TraceTarget = string | RegExp;

export interface SentryInitOptions {
  dsn: string | undefined;
  environment: string;
  release: string;
  /**
   * Hosts whose outbound requests should carry the `sentry-trace` / `baggage`
   * propagation headers. Anything not matching is left alone, so we don't leak
   * trace context to third-party origins.
   */
  apiHosts: TraceTarget[];
  tracesSampleRate: number;
}

/**
 * Boots `@sentry/react` for one of the SPAs. With an empty `dsn` this is a
 * no-op so local dev doesn't need Sentry credentials. Apps must call this
 * before `ReactDOM.createRoot(...)` so the BrowserTracing integration sees
 * the very first navigation event.
 */
export function initSentry(opts: SentryInitOptions): void {
  if (!opts.dsn) {
    return;
  }
  Sentry.init({
    dsn: opts.dsn,
    environment: opts.environment,
    release: opts.release,
    // Sentry v11 replaced `sendDefaultPii` with per-category `dataCollection`,
    // and every category defaults to ON. Keep the v10 `sendDefaultPii: false`
    // posture explicitly: no user/IP inference, cookies, headers, bodies, or
    // query params (invite/share tokens ride query strings — ADR-030 D18).
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
    },
    propagateTraceparent: true,
    tracesSampleRate: opts.tracesSampleRate,
    tracePropagationTargets: opts.apiHosts,
    beforeSend: (event) => scrubSentryEvent(event),
    beforeSendTransaction: (event) => scrubSentryTransaction(event),
    beforeBreadcrumb: (breadcrumb) => scrubSentryBreadcrumb(breadcrumb),
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
  });
}

export function tracePropagationTargets(
  apiURL: string | undefined,
): TraceTarget[] {
  if (!apiURL) {
    return [/^\/api\//];
  }
  return [/^\/api\//, apiURL];
}

export function scrubSentryEvent(
  event: ErrorEvent,
  location: string | undefined = currentBrowserLocation(),
): ErrorEvent | null {
  if (isKnownExternalUnhandledRejection(event)) {
    return null;
  }

  if (
    isSensitiveLocation(location) ||
    isSensitiveLocation(event.request?.url) ||
    isSensitiveLocation(event.transaction) ||
    hasSensitiveLocation(event.request) ||
    hasSensitiveLocation(event.breadcrumbs) ||
    hasSensitiveLocation(event.tags)
  ) {
    return null;
  }

  if (event.request) {
    delete event.request.data;
    delete event.request.cookies;
    for (const header of [
      "authorization",
      "cookie",
      "set-cookie",
      "x-clerk-auth-token",
      "x-svix-signature",
    ]) {
      delete event.request.headers?.[header];
      delete event.request.headers?.[header.toUpperCase()];
      delete event.request.headers?.[toHeaderCase(header)];
    }
  }
  if (event.user) {
    delete event.user.email;
    delete event.user.username;
    delete event.user.name;
    delete event.user.ip_address;
  }
  return event;
}

/** Drop route spans rather than emitting a transaction with a claim token. */
export function scrubSentryTransaction<T extends { transaction?: string }>(
  event: T,
  location: string | undefined = currentBrowserLocation(),
): T | null {
  if (
    isSensitiveLocation(location) ||
    isSensitiveLocation(event.transaction) ||
    hasSensitiveLocation(event)
  ) {
    return null;
  }
  return event;
}

/**
 * Navigation and DOM-click breadcrumbs are automatic telemetry. On a claim
 * route they can contain the opaque URL or revealed CTA/name, so drop them as
 * a whole rather than attempting fragile text replacement.
 */
export function scrubSentryBreadcrumb(
  breadcrumb: Breadcrumb,
  location: string | undefined = currentBrowserLocation(),
): Breadcrumb | null {
  if (isSensitiveLocation(location) || hasSensitiveLocation(breadcrumb.data)) {
    return null;
  }
  return breadcrumb;
}

function isKnownExternalUnhandledRejection(event: ErrorEvent): boolean {
  return (
    event.exception?.values?.some(
      (exception) =>
        exception.type === "UnhandledRejection" &&
        exception.mechanism?.type ===
          "auto.browser.global_handlers.onunhandledrejection" &&
        isChromeExtensionObjectNotFoundRejection(exception.value),
    ) ?? false
  );
}

function isChromeExtensionObjectNotFoundRejection(
  value: string | undefined,
): boolean {
  return (
    typeof value === "string" &&
    /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:update, ParamCount:\d+$/.test(
      value,
    )
  );
}

function toHeaderCase(header: string): string {
  return header
    .split("-")
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join("-");
}

export { Sentry };
